import { createFileRoute, Link } from '@tanstack/react-router'
import { ArrowRight } from 'lucide-react'
import { article, jsonLdScript } from '@/lib/schema'

export const Route = createFileRoute('/_site/guides/switch-from-uptimerobot')({
  component: SwitchGuidePage,
  head: () => ({
    meta: [
      {
        title: 'Switch from UptimeRobot to Uptrack (bulk import)',
      },
      {
        name: 'description',
        content:
          'Move your monitors from UptimeRobot to Uptrack in a few minutes — export to CSV, drag it in, and Uptrack recreates every monitor. No manual re-entry.',
      },
      { property: 'og:title', content: 'Switch from UptimeRobot to Uptrack — bulk import' },
      {
        property: 'og:description',
        content: 'Export your UptimeRobot monitors to CSV and bulk-import them into Uptrack — no recreating them by hand.',
      },
      {
        property: 'og:image',
        content: 'https://uptrack.app/og/guides-switch-from-uptimerobot.png',
      },
    ],
    links: [{ rel: 'canonical', href: 'https://uptrack.app/guides/switch-from-uptimerobot' }],
    scripts: [
      jsonLdScript(
        article({
          headline: 'Switch from UptimeRobot to Uptrack (bulk import)',
          description: 'Move your monitors from UptimeRobot to Uptrack in a few minutes — export to CSV, drag it in, and Uptrack recreates every monitor. No manual re-entry.',
          url: 'https://uptrack.app/guides/switch-from-uptimerobot',
          datePublished: '2026-06-13',
          imageUrl: 'https://uptrack.app/og/guides-switch-from-uptimerobot.png',
        }),
      ),
      {
        type: 'application/ld+json',
        children: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'HowTo',
          name: 'Switch from UptimeRobot to Uptrack',
          description:
            'Export your monitors from UptimeRobot and bulk-import them into Uptrack with a CSV.',
          step: [
            { '@type': 'HowToStep', name: 'Get your monitors as a CSV' },
            { '@type': 'HowToStep', name: 'Import the CSV into Uptrack' },
            { '@type': 'HowToStep', name: 'Set up alerts and go' },
          ],
        }),
      },
    ],
  }),
})

function SwitchGuidePage() {
  return (
    <>
      <>
        <article
          className="article-prose container mx-auto max-w-2xl px-4 py-16"
        >
          <p className="font-sans mb-3 text-sm font-medium uppercase tracking-wide text-primary">Guide</p>
          <h1 className="font-sans mb-4 text-4xl font-semibold">Switch from UptimeRobot to Uptrack</h1>
          <p className="mb-8 text-lg leading-[1.7] text-muted-foreground">
            The part that stops most people from moving is the thought of recreating forty monitors by hand. You
            don&rsquo;t have to. Export your list once, drop it into Uptrack, and every monitor comes across in a
            few minutes.
          </p>

          <section className="mb-10">
            <h2 className="font-sans mb-4 text-2xl font-semibold">Step 1 — Get your monitors as a CSV</h2>
            <p className="mb-4 text-lg leading-[1.7] text-body">
              Uptrack&rsquo;s importer reads a plain CSV with a column for the monitor name, the URL, and the type.
              You can pull that from UptimeRobot&rsquo;s API (the <code className="font-mono text-base">getMonitors</code>{' '}
              endpoint returns every monitor with its friendly name, URL, type, and interval), or just build the
              file yourself in a spreadsheet. A typical export looks like:
            </p>
            <pre tabIndex={0} className="mb-4 overflow-x-auto rounded-lg bg-foreground/[0.06] p-4 font-mono text-sm">
              <code>{`Friendly Name,URL,Type,Monitoring Interval (s)
Marketing site,https://example.com,HTTP(s),60
Checkout API,https://api.example.com/health,Keyword,60
DNS,example.com,Ping,300`}</code>
            </pre>
            <p className="text-lg leading-[1.7] text-body">
              The headers don&rsquo;t have to match exactly — Uptrack auto-detects UptimeRobot, Pingdom, and
              generic column names (name / URL / type / interval / keyword), so an export from any of them, or a
              file you typed by hand, all work.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="font-sans mb-4 text-2xl font-semibold">Step 2 — Import it into Uptrack</h2>
            <p className="mb-4 text-lg leading-[1.7] text-body">
              In your dashboard, open <strong className="text-foreground">Uptime Monitors → Import</strong>, and
              drop the CSV onto the page. Uptrack parses it client-side, shows you a preview of every monitor it
              found, and lets you confirm before anything is created.
            </p>
            <p className="text-lg leading-[1.7] text-body">
              On import it skips any URL you&rsquo;re already monitoring (so re-running the same file is safe),
              fills up to your plan&rsquo;s monitor limit, and reports exactly what was created, skipped, or had a
              problem. No half-finished state, no duplicates.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="font-sans mb-4 text-2xl font-semibold">Step 3 — Set up alerts and go</h2>
            <p className="mb-4 text-lg leading-[1.7] text-body">
              Monitors come over with their URL, type, and interval. The last step is telling Uptrack where to
              reach you: add an alert channel (email, Slack, Discord, and Telegram are on every plan; the free plan has email plus one chat
              channel) under <strong className="text-foreground">Alerts</strong>, and you&rsquo;re done.
            </p>
            <p className="text-lg leading-[1.7] text-body">
              A couple of things you get on the way in: every plan runs checks at 30-second intervals,
              and an alert only fires after consecutive checks from multiple regions agree it&rsquo;s really down —
              so the move usually comes with fewer false alarms, not more.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="font-sans mb-4 text-2xl font-semibold">Prefer it in version control?</h2>
            <p className="text-lg leading-[1.7] text-body">
              If you&rsquo;d rather keep your monitors as code, the same data works as a YAML file you can{' '}
              <code className="font-mono text-base">apply</code> from CI with the Uptrack CLI — define monitors in
              a file, review changes in a pull request, and sync on merge. The CSV import is the fastest way to get
              started; the CLI is there when you want it.
            </p>
          </section>

          <div className="font-sans rounded-lg border border-border bg-muted p-6 sm:p-8">
            <h2 className="text-xl font-semibold">Bring your monitors over</h2>
            <p className="mt-1 text-sm text-body">
              Pro includes 50 monitors at 30-second checks. Import your CSV and you&rsquo;re running in minutes.
            </p>
            <Link
              to="/signup"
              className="mt-4 inline-flex items-center font-medium text-primary hover:underline"
            >
              Get started <ArrowRight className="ml-1 h-4 w-4" />
            </Link>
            <span className="mx-3 text-muted-foreground">·</span>
            <Link to="/compare/uptimerobot" className="font-medium text-primary hover:underline">
              Uptrack vs UptimeRobot
            </Link>
            <span className="mx-3 text-muted-foreground">·</span>
            <Link to="/guides/switch-from-pingdom" className="font-medium text-primary hover:underline">
              Coming from Pingdom?
            </Link>
          </div>
        </article>
      </>
      </>
  )
}
